/* @jsx h */
import { LemmonModal } from "@app/vue/components/UIComponents";
import { sortTables } from "@lemmonhq/orders";
import { FloorPlanTable, TableStatus, FloorPlan } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { defineComponent, ref, h } from "vue";
import { deleteFloorPlanItem } from "./utils";

export const FloorPlanItemForm = defineComponent<{
  onClose: Function;
  tables: TableStatus[];
  planConfig: FloorPlanTable;
  floorPlan: FloorPlan;
  onSelect: (item: FloorPlanTable) => void;
  onUpdate: (floorPlan: FloorPlan) => void;
}>({
  name: "FloorPlanItemForm",
  props: [
    "onClose",
    "tables",
    "onSelect",
    "planConfig",
    "floorPlan",
    "onUpdate",
  ],
  setup(props) {
    const selectedType = ref<"cashier" | "bar" | "table" | null>(
      props.planConfig.type || "table",
    );
    const selectedShape = ref<"rectangle" | "round">(
      props.planConfig.shape || "rectangle",
    );
    const selectedTableId = ref<number | null>(
      props.planConfig.tableId || null,
    );
    const selectedSeats = ref<number | null>(props.planConfig.seats || null);
    const deleting = ref(false);
    const { t } = useLocalization();
    const icons: Record<string, string> = {
      cashier: "point_of_sale",
      bar: "local_bar",
      table: "table_restaurant",
    };
    const shapeIcons: Record<string, string> = {
      rectangle: "rectangle",
      round: "circle",
    };

    const handleDelete = async () => {
      if (!props.onUpdate) return;

      try {
        deleting.value = true;
        const updatedFloorPlan = deleteFloorPlanItem(
          props.floorPlan,
          props.planConfig.id,
        );
        props.onUpdate(updatedFloorPlan);
        props.onClose();
      } catch (error) {
        console.error("Failed to delete floor plan item:", error);
      } finally {
        deleting.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("pos.pay.select_table")}
        renderButtons={() => (
          <div class="px-5 py-3.5 border-t border-base-content/10">
            <div class="flex items-center justify-between gap-2">
              <button
                onClick={handleDelete}
                class="btn btn-sm btn-ghost text-error hover:bg-error/10"
                disabled={deleting.value}
              >
                {deleting.value ? (
                  <span class="loading loading-spinner loading-xs"></span>
                ) : (
                  <span class="material-symbols-outlined text-lg">delete</span>
                )}
              </button>
              <div class="flex gap-2">
                <button
                  onClick={() => props.onClose()}
                  class="btn btn-sm btn-ghost"
                  type="button"
                  disabled={deleting.value}
                >
                  {t("general.words.cancel")}
                </button>
                <button
                  class="btn btn-sm btn-primary"
                  type="submit"
                  disabled={!selectedType.value || deleting.value}
                  onClick={() => {
                    const planConfig = props.planConfig;
                    const existingTableDef = props.floorPlan.plan.find(
                      (el) => el.id === planConfig.id,
                    );

                    if (!existingTableDef) return;

                    const tableDef: FloorPlanTable = {
                      ...existingTableDef,
                      type: selectedType.value!,
                      shape:
                        selectedType.value === "table"
                          ? selectedShape.value
                          : undefined,
                      seats:
                        selectedType.value === "table" && selectedSeats.value
                          ? selectedSeats.value
                          : undefined,
                      tableId:
                        selectedType.value === "table" && selectedTableId.value
                          ? selectedTableId.value
                          : undefined,
                    };

                    // Round tables can be any size (oval when non-square)
                    props.onSelect(tableDef);
                  }}
                >
                  <span>{t("general.words.confirm")}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        onClose={props.onClose}
      >
        <div class="px-5 py-4 space-y-5">
          {/* Type selector — icon buttons */}
          <div>
            <label class="text-xs font-semibold text-base-content/40 uppercase tracking-wider mb-2 block">
              Type
            </label>
            <div class="flex gap-2">
              {(["table", "cashier", "bar"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    selectedType.value = type;
                    if (type !== "table") selectedTableId.value = null;
                  }}
                  class={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    selectedType.value === type
                      ? "bg-primary text-accent"
                      : "bg-base-200 text-base-content/70 hover:bg-base-300"
                  }`}
                >
                  <span class="material-symbols-outlined text-lg">
                    {icons[type]}
                  </span>
                  <span class="capitalize">{type}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Shape selector — only for tables */}
          {selectedType.value === "table" && (
            <div>
              <label class="text-xs font-semibold text-base-content/40 uppercase tracking-wider mb-2 block">
                Shape
              </label>
              <div class="flex gap-2">
                {(["rectangle", "round"] as const).map((shape) => (
                  <button
                    key={shape}
                    type="button"
                    onClick={() => {
                      selectedShape.value = shape;
                    }}
                    class={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                      selectedShape.value === shape
                        ? "bg-primary text-accent"
                        : "bg-base-200 text-base-content/70 hover:bg-base-300"
                    }`}
                  >
                    <span class="material-symbols-outlined text-lg">
                      {shapeIcons[shape]}
                    </span>
                    <span class="capitalize">{shape}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Table select + seats in a row */}
          {selectedType.value === "table" && (
            <div class="flex gap-3">
              <div class="flex-1">
                <label class="text-xs font-semibold text-base-content/40 uppercase tracking-wider mb-2 block">
                  Table
                </label>
                <select
                  class="select select-sm select-bordered w-full bg-base-200 border-base-content/10"
                  style="height: 38px; min-height: 38px;"
                  value={selectedTableId.value || ""}
                  onChange={(e) => {
                    const val = (e.target as HTMLSelectElement).value;
                    selectedTableId.value = val ? Number(val) : null;
                  }}
                >
                  <option value="">Select a table...</option>
                  {sortTables(props.tables).map((table) => (
                    <option key={table.table.id} value={table.table.id}>
                      {table.table.name}
                      {table.table.is_busy ? " (Busy)" : ""}
                      {table.party ? ` — ${table.party?.name}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div class="w-28">
                <label class="text-xs font-semibold text-base-content/40 uppercase tracking-wider mb-2 block">
                  Seats
                </label>
                <div
                  class="flex items-center gap-0 bg-base-200 rounded-lg border border-base-content/10"
                  style="height: 38px;"
                >
                  <button
                    type="button"
                    class="w-8 h-full flex items-center justify-center text-base-content/50 hover:text-base-content transition-colors"
                    onClick={() => {
                      if (
                        selectedSeats.value !== null &&
                        selectedSeats.value > 1
                      )
                        selectedSeats.value--;
                      else if (selectedSeats.value === 1)
                        selectedSeats.value = null;
                    }}
                  >
                    <span class="material-symbols-outlined text-sm">
                      remove
                    </span>
                  </button>
                  <input
                    type="text"
                    inputmode="numeric"
                    class="flex-1 w-full text-center bg-transparent border-none text-sm focus:outline-none"
                    style="height: 38px; min-height: 38px;"
                    placeholder="Auto"
                    value={selectedSeats.value || ""}
                    onInput={(e) => {
                      const val = parseInt(
                        (e.target as HTMLInputElement).value,
                      );
                      selectedSeats.value = isNaN(val)
                        ? null
                        : Math.max(1, Math.min(20, val));
                    }}
                  />
                  <button
                    type="button"
                    class="w-8 h-full flex items-center justify-center text-base-content/50 hover:text-base-content transition-colors"
                    onClick={() => {
                      selectedSeats.value = (selectedSeats.value || 0) + 1;
                    }}
                  >
                    <span class="material-symbols-outlined text-sm">add</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </LemmonModal>
    );
  },
});
